import { defineStore } from 'pinia'
import { addHours, format } from 'date-fns'
import { ru } from 'date-fns/locale'
import {
  isEmptyObject,
  getTimezoneOffsetInHours,
  isNetworkDisconnect,
} from 'www/utils'
import { apiScheduleTelemed } from 'www/api'
import {
  getEditableCalendar,
  getInitCalendarCommonData,
  getInitCalendarDoctorData,
  getInitCalendarServicesData,
  getSelectedDate,
} from 'www/vue_components/common/AppointmentPage/interfaces'
import { getCalendarData } from 'www/vue_components/common/AppointmentPage/api'
import { findAvailableSlotBySchedule } from 'www/vue_components/common/AppointmentPage/functions'
import { APPOINTMENT_ERRORS } from 'www/vue_components/common/AppointmentPage/constants'
/* eslint-disable import/no-cycle */
import useLpuDataStore from 'www/vue_components/common/AppointmentPage/stores/useLpuDataStore'
import useGlobalDataStore from 'www/vue_components/common/AppointmentPage/stores/useGlobalDataStore'
import useDoctorDataStore from 'www/vue_components/common/AppointmentPage/stores/useDoctorDataStore'
import useServiceDataStore from 'www/vue_components/common/AppointmentPage/stores/useServiceDataStore'
import useAppointmentDataStore from 'www/vue_components/common/AppointmentPage/stores/useAppointmentDataStore'
import useFamilyProfilesStore from 'www/vue_components/common/FamilyProfiles/stores/useFamilyProfilesStore'
import usePreliminaryConsultationStore from 'www/vue_components/common/AppointmentPage/stores/usePreliminaryConsultationStore'
/* eslint-enable import/no-cycle */
import { useDoctorAutomaticScheduleChecks } from 'www/vue_components/common/AppointmentPage/subStores'

const useCalendarDataStore = defineStore('calendarDataStore', {
  state: () => ({
    initCalendarData: {
      ...getInitCalendarCommonData(),
      ...getInitCalendarServicesData(),
      ...getInitCalendarDoctorData(),
    },
    editableCalendar: getEditableCalendar(),
    lastSelectedDate: getSelectedDate(),
    isLoadingSlots: false,
    appointmentCalendarEl: null,
    isZeroRequests: true,
    isErrorState: false,
    isNearestSlotsRequested: false,
  }),
  getters: {
    hasSlotsRequestData() {
      const serviceDataStore = useServiceDataStore()
      const appointmentDataStore = useAppointmentDataStore()

      if (appointmentDataStore.appointmentObjectTypes.isServices) {
        return Boolean(this.initCalendarData.synonymId && serviceDataStore.serviceData.hasIntervals)
      }

      if (appointmentDataStore.appointmentObjectTypes.isDoctor) {
        return Boolean(this.initCalendarData.doctorsAndLpus?.length && this.initCalendarData.lpuTimedeltas?.length)
      }

      return false
    },
    lastSelectedDateString() {
      if (this.lastSelectedDate.day && this.lastSelectedDate.time) {
        return `${this.lastSelectedDate.day} ${this.lastSelectedDate.time}`
      }

      return ''
    },
    editableCalendarTitle() {
      const { time, dateName } = this.isCalendarDateSelected
        ? this.editableCalendar.selectedDate
        : this.initCalendarData

      if (time && dateName) {
        return `${dateName.toLowerCase()}, ${time}`
      }

      return ''
    },
    isEmptyCalendarList() {
      if (!this.editableCalendar.calendarList) {
        return true
      }

      return isEmptyObject(this.editableCalendar.calendarList)
        || Object.values(this.editableCalendar.calendarList).every(slots => !slots.length)
    },
    isValidCalendar() {
      if (this.isLoadingSlots) {
        return false
      }

      const appointmentDataStore = useAppointmentDataStore()
      const isNotDirect = !appointmentDataStore.appointmentRegistrationTypes.isDirect
        || appointmentDataStore.useServiceExpress
      const doctorAutomaticScheduleChecks = useDoctorAutomaticScheduleChecks()

      if (
        appointmentDataStore.appointmentObjectTypes.isDoctor
        && !this.isEmptyCalendarList
      ) {
        if (this.isCalendarDateSelected) {
          const { day, time } = this.editableCalendar.selectedDate

          const hasTime = doctorAutomaticScheduleChecks.testHasTime({
            selectedDate: { day, time },
          })

          return hasTime || this.isAvailableSlot || (isNotDirect && this.isEmptyCalendarList)
        }
      }

      return this.isAvailableSlot || (isNotDirect && this.isEmptyCalendarList)
    },
    isCalendarDateSelected() {
      const { day, time } = this.editableCalendar.selectedDate

      return Boolean(day && time)
    },
    selectedSlotDate() {
      if (!this.isCalendarDateSelected) {
        return null
      }

      const { day, time } = this.editableCalendar.selectedDate

      return new Date(`${day}T${time}`)
    },
    selectedFullDate() {
      if (!this.selectedSlotDate) {
        return ''
      }

      return format(this.selectedSlotDate, 'dd.MM.yyyy HH:mm', { locale: ru })
    },
    isAvailableSlot() {
      const appointmentDataStore = useAppointmentDataStore()

      if (this.editableCalendar.selectedDate.isSlotFixed) {
        return true
      }

      // Не проверяем доступность слота по расписанию клиники для ПЗ
      if (
        appointmentDataStore.appointmentRegistrationTypes.isDirect
        && !appointmentDataStore.useServiceExpress
      ) {
        return !this.isEmptyCalendarList && this.isCalendarDateSelected
      }

      if (!this.isCalendarDateSelected || !this.nearestFreeAppointmentDate || this.isEmptyCalendarList) {
        return false
      }

      const { day, time } = this.editableCalendar.selectedDate
      const selectedSlotDate = new Date(`${day}T${time}`)

      return selectedSlotDate >= this.nearestFreeAppointmentDate
    },
    nearestFreeAppointmentDate() {
      if (this.isEmptyCalendarList || this.isLoadingSlots) {
        return null
      }

      const lpuDataStore = useLpuDataStore()
      const appointmentDataStore = useAppointmentDataStore()

      const { times: callTimes, minHoursToCall } = appointmentDataStore.appointmentCallTimes
      const { calendarList } = this.editableCalendar
      let nearestFreeAppointmentDate = null
      const { schedule } = lpuDataStore.lpuData

      if (!callTimes.length) {
        const [day, slots] = Object.entries(calendarList).find(([, slotList]) => slotList.length) || []

        if (!slots) {
          return null
        }

        return new Date(`${day}T${slots[0].time}`)
      }

      const minAvailableDate = addHours(new Date(callTimes[0].date), minHoursToCall)
      const minAvailableDayDate = new Date(minAvailableDate)

      minAvailableDayDate.setUTCHours(0, 0, 0, 0)

      Object.entries(calendarList).some(([day, slots]) => {
        if (new Date(day) < minAvailableDayDate) {
          return false
        }

        const availableSlot = findAvailableSlotBySchedule({
          day,
          slots,
          schedule,
          minDate: minAvailableDate,
        })

        if (availableSlot) {
          nearestFreeAppointmentDate = new Date(`${day}T${availableSlot.time}`)
        }

        return nearestFreeAppointmentDate
      })

      return nearestFreeAppointmentDate
    },
  },
  actions: {
    updateCalendarIsChanging(payload) {
      this.editableCalendar.isChanging = payload
    },
    updateCalendarList(payload) {
      this.editableCalendar.calendarList = payload
    },
    updateHasExcludedDoctors(payload) {
      this.editableCalendar.hasExcludedDoctors = payload
    },
    updateSelectedDate(payload) {
      this.editableCalendar.selectedDate = {
        ...this.editableCalendar.selectedDate,
        ...payload,
      }
    },
    resetSelectedDate() {
      this.editableCalendar.selectedDate = {}
    },
    updateIsLoadingSlots(payload) {
      this.isLoadingSlots = !!payload
    },
    updateAppointmentCalendarEl(payload) {
      this.appointmentCalendarEl = payload
    },
    updateLastSelectedDate(payload) {
      this.lastSelectedDate = payload
    },
    updateIsZeroRequests(payload) {
      this.isZeroRequests = payload
    },
    updateIsErrorState(payload) {
      this.isErrorState = payload
    },
    validateCalendar() {
      this.updateIsErrorState(!this.isValidCalendar)

      if (!this.isValidCalendar) {
        const appointmentDataStore = useAppointmentDataStore()

        if (appointmentDataStore.applyServiceEmptySlotsFallback()) {
          // UI уже на телефоне/заглушке — блокируем сабмит без скролла к календарю
          return { blockSubmitWithoutScroll: true }
        }

        return this.appointmentCalendarEl || appointmentDataStore.appointmentNode
      }
    },
    requestForSlots({ afterRequestCallback = () => {}, errorRequestCallback = () => {} } = {}) {
      const doctorDataStore = useDoctorDataStore()
      const serviceDataStore = useServiceDataStore()
      const appointmentDataStore = useAppointmentDataStore()
      const preliminaryConsultationStore = usePreliminaryConsultationStore()
      const familyProfilesStore = useFamilyProfilesStore()

      if (appointmentDataStore.appointmentRegistrationTypes.isExpress) {
        return
      }

      this.updateIsLoadingSlots(true)

      if (!this.hasSlotsRequestData) {
        errorRequestCallback()
        this.updateIsLoadingSlots(false)

        return
      }

      const { appointmentObjectTypes, appointmentPlaceTypes } = appointmentDataStore

      let requestData = {}

      if (appointmentObjectTypes.isServices) {
        const service = {
          service_id: this.initCalendarData.synonymId,
          lpu_id: this.initCalendarData.lpuId,
        }

        if (preliminaryConsultationStore.isPreliminaryConsultation) {
          const {
            selectedConsultingDoctor,
            selectedDoctorConsultation,
          } = preliminaryConsultationStore

          if (selectedConsultingDoctor.id) {
            service.doctor_id = selectedConsultingDoctor.id
          }

          if (selectedDoctorConsultation.id) {
            service.service_id = selectedDoctorConsultation.id
            service.service_requires_consultation_id = this.initCalendarData.synonymId
          }
        } else {
          if (serviceDataStore.selectedEquipment.id) {
            service.device_id = serviceDataStore.selectedEquipment.id
          }

          if (doctorDataStore.selectedDoctor.id) {
            service.doctor_id = doctorDataStore.selectedDoctor.id
          }
        }

        requestData = {
          age: familyProfilesStore.selectedPatient.ageNumber,
          days: this.initCalendarData.days,
          day_start: this.initCalendarData.dateNow,
          only_free: true,
          town_timedelta: this.initCalendarData.timedelta,
          services: [service],
        }
      } else if (appointmentObjectTypes.isDoctor) {
        if ((appointmentPlaceTypes.isTelemed || appointmentPlaceTypes.isTelemedMedtochka)) {
          /** @type {import('www/api/apiScheduleTelemed.types').ApiScheduleTelemedParamsPost} */
          const params = {
            user_start_date: this.initCalendarData.dateNow,
            days: this.initCalendarData.days,
            user_timezone: getTimezoneOffsetInHours(),
            only_free: true,
            lpu_params: this.initCalendarData.doctorsAndLpus.map(({ doctorId, lpuId }) => ({
              lpu_id: lpuId,
              schedule_params: [{
                doctor_id: doctorId,
                type_schedule: doctorDataStore.scheduleType,
              }],
            })),
          }

          requestData = params
        } else {
          requestData = {
            days: this.initCalendarData.days,
            dt_start: this.initCalendarData.dateNow,
            doctors_lpus: this.initCalendarData.doctorsAndLpus.map(({ doctorId, lpuId }) => ({
              doctor_id: doctorId,
              lpu_id: lpuId,
            })),
            lpu_timedelta: this.initCalendarData.lpuTimedeltas,
          }
        }
      }

      getCalendarData({ requestData, appointmentObjectTypes, appointmentPlaceTypes })
        .finally(() => {
          this.updateIsLoadingSlots(false)
          this.updateIsZeroRequests(false)
        })
        .then(response => {
          let slots = {}

          if (appointmentPlaceTypes.isTelemed || appointmentPlaceTypes.isTelemedMedtochka) {
            /** @type {import('www/api/apiScheduleTelemed.types').ApiScheduleTelemedResponsePost} */
            const responseData = response.data

            slots = apiScheduleTelemed.transformSlots(responseData[0].schedule_data[0]?.schedule)
          } else {
            slots = response.result?.[0]?.slots

            this.updateHasExcludedDoctors(response.has_excluded_doctors)
          }

          this.updateCalendarList(slots)

          const { day: selectedDay, time: selectedTime } = this.editableCalendar.selectedDate
          const haveSelectedDate = this.editableCalendar.calendarList
              && this.editableCalendar.calendarList[selectedDay]?.some(({ time }) => time === selectedTime)

          if (!haveSelectedDate) {
            this.resetSelectedDate()
          }

          if (!this.isValidCalendar) {
            this.updateCalendarIsChanging(true)
          }

          if (
            this.isEmptyCalendarList
            && appointmentDataStore.appointmentObjectTypes.isServices
            && appointmentDataStore.appointmentRegistrationTypes.isDirect
          ) {
            if (appointmentDataStore.applyServiceEmptySlotsFallback({ forceEmptySlots: true })) {
              return
            }

            if (!appointmentDataStore.useServiceExpress) {
              errorRequestCallback()
              return
            }
          }

          afterRequestCallback()
        })
        .catch(({ message, response } = {}) => {
          const { status } = response || {}

          if (isNetworkDisconnect({ status, message })) {
            const globalDataStore = useGlobalDataStore()

            globalDataStore.updateErrorData({
              isEnabled: true,
              isDialogRender: true,
              ...APPOINTMENT_ERRORS.notNetwork,
            })
            this.updateIsLoadingSlots(true)

            return
          }

          errorRequestCallback()
        })
    },
    async startRecursiveUpdateDoctorSchedule() {
      try {
        if (this.isEmptyCalendarList) {
          return
        }

        const doctorDataStore = useDoctorDataStore()
        const globalDataStore = useGlobalDataStore()
        const appointmentDataStore = useAppointmentDataStore()
        const doctorAutomaticScheduleChecks = useDoctorAutomaticScheduleChecks()

        if (appointmentDataStore.appointmentRegistrationTypes.isExpress) {
          return
        }

        const { appointmentPlaceTypes } = appointmentDataStore
        let requestData
        let requestDataTelemed

        if (appointmentPlaceTypes.isTelemed || appointmentPlaceTypes.isTelemedMedtochka) {
          /** @type {import('www/api/apiScheduleTelemed.types').ApiScheduleTelemedParamsPost} */
          const params = {
            user_start_date: format(new Date(), 'yyyy-MM-dd'),
            days: this.initCalendarData.days,
            user_timezone: getTimezoneOffsetInHours(),
            only_free: true,
            lpu_params: this.initCalendarData.doctorsAndLpus.map(({ doctorId, lpuId }) => ({
              lpu_id: lpuId,
              schedule_params: [{
                doctor_id: doctorId,
                type_schedule: doctorDataStore.scheduleType,
              }],
            })),
          }

          requestDataTelemed = params
        } else {
          requestData = {
            days: this.initCalendarData.days,
            dt_start: this.initCalendarData.dateNow,
            doctors_lpus: this.initCalendarData.doctorsAndLpus.map(({ doctorId, lpuId }) => ({
              doctor_id: doctorId,
              lpu_id: lpuId,
            })),
            lpu_timedelta: this.initCalendarData.lpuTimedeltas,
          }
        }

        await doctorAutomaticScheduleChecks.requestRecursiveDoctorSchedule({
          requestData,
          requestDataTelemed,
          afterEach: () => {
            try {
              this.updateCalendarList(doctorAutomaticScheduleChecks.slots)
            } catch {
              this.updateCalendarList({})
            }

            try {
              const { day, time } = this.editableCalendar.selectedDate

              if (!this.isEmptyCalendarList && day && time) {
                const selectedDate = { day, time }
                const hasTime = doctorAutomaticScheduleChecks.testHasTime({ selectedDate })

                if (!hasTime) {
                  this.updateIsErrorState(true)
                  this.updateCalendarIsChanging(true)
                }
              }
            } catch { /* empty */ } finally {
              if (globalDataStore.errorData.isEnabled || globalDataStore.successData.isEnabled) {
                doctorAutomaticScheduleChecks.useStopRequest = true
              }
            }
          },
          afterAll: () => {
            try {
              this.updateCalendarList(doctorAutomaticScheduleChecks.slots)
            } catch {
              this.updateCalendarList({})
            }

            try {
              if (globalDataStore.errorData.isEnabled || globalDataStore.successData.isEnabled) {
                return
              }

              if (this.isEmptyCalendarList) {
                this.updateIsErrorState(true)
                this.updateCalendarIsChanging(true)

                return
              }

              const { day, time } = this.editableCalendar.selectedDate

              if (!this.isEmptyCalendarList && day && time) {
                const selectedDate = { day, time }
                const hasTime = doctorAutomaticScheduleChecks.testHasTime({ selectedDate })

                if (!hasTime) {
                  this.updateIsErrorState(true)
                  this.updateCalendarIsChanging(true)

                  if (doctorAutomaticScheduleChecks.isAttemptsOver) {
                    globalDataStore.updateErrorData({
                      isEnabled: true,
                      isPersistent: true,
                      isDialogRender: true,
                      ...APPOINTMENT_ERRORS.dataIsOutDate,
                    })
                  }

                  return
                }
              }

              globalDataStore.updateErrorData({
                isEnabled: true,
                isPersistent: true,
                isDialogRender: true,
                ...APPOINTMENT_ERRORS.dataIsOutDate,
              })
            } catch { /* empty */ }
          },
        })
      } catch { /* empty */ }
    },
  },
})

export default useCalendarDataStore
